import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ValidatorUtils from 'services/ValidatorUtils';
import './style.scss';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import DefaultTextField from 'components/Utils/DefaultTextField';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import { DefaultSwitch } from 'services/UIUtils';
import { chargePatientSchema, ChargePatientValues } from 'types/forms';

export const HST = 0.13;

const ChargePatientForm = ({ patient, onSuccess }: { patient: any; onSuccess?: () => void }) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useForm<ChargePatientValues>({
    resolver: zodResolver(chargePatientSchema),
    defaultValues: { amount: '', description: '', memo: '', tax_exempt: false },
    mode: 'onChange',
  });

  const amount = watch('amount');
  const taxExempt = watch('tax_exempt');

  async function onCharge(values: ChargePatientValues) {
    const { amount, description, memo, tax_exempt } = values;
    const paymentCharge = { amount, description, memo, tax_exempt };

    setLoading(true);
    const result = await dispatch(
      api.Payments.chargePatient({ patientId: patient.id, paymentCharge }),
      {
        title: 'Charge Patient',
        successText: 'Patient successfully charged $' + amount,
      },
    );
    setLoading(false);
    if (result && onSuccess) onSuccess();
  }

  let total = null;

  if (amount && isValid && ValidatorUtils.isDollarAmount(amount)) {
    let parsedAmount = parseFloat(amount.replace(',', '.'));

    if (!taxExempt) {
      parsedAmount *= 1 + HST;
    }

    total = `$${parseFloat((Math.round(parsedAmount * 100) / 100).toString()).toFixed(2)}`;
  }

  return (
    <div className="charge-patient-form">
      <DefaultTextField name="amount" control={control} label="Amount" style={{ width: '100%' }} />
      <DefaultTextField
        name="description"
        control={control}
        label="Description"
        style={{ width: '100%' }}
      />
      <span className="small-title">Memo</span>
      <TextareaAutosize name="memo" control={control} />
      <DefaultSwitch name="tax_exempt" control={control} label="Tax Exempt" />
      <div className="submit">
        <span className="total">
          <b>Total: </b>
          {total}
        </span>
        <DefaultButton
          name="Submit"
          loading={loading}
          disabled={!isValid}
          onClick={handleSubmit(onCharge)}
        />
      </div>
    </div>
  );
};

export default ChargePatientForm;
